import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Col,
  Row,
  NMForm,
  Button,
  usePub,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  mitigatorActions,
  getCurrentVulnerability,
  getThreatsByStatus,
} from "../../../store/";

const getVulnerabilityFields = (isViewMode, isEditMode) => {
  const fields = [];

  // Only show ID in view OR edit mode
  if (isViewMode || isEditMode) {
    fields.push({
      label: "ID",
      labelAlign: "top",
      attribute: "vulnerability_id",
      placeholder: "",
      readOnly: true,
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-91-px" },
    });
  }

  // Always include the remaining fields
  fields.push(
    {
      label: "Category",
      labelAlign: "top",
      attribute: "category",
      placeholder: "Category",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-91-px" },
    },
    {
      label: "Threat",
      labelAlign: "top",
      attribute: "threat",
      placeholder: "Threat",
      typeRepresentation: isViewMode ? "TEXT" : "DROPDOWN",
      classes: {
        root: "w-100",
        input: "bg-contrast",
        label: "text-right w-91-px",
      },
    },
    {
      label: "Vulnerability",
      labelAlign: "top",
      attribute: "vulnerability",
      placeholder: "Vulnerability",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-91-px" },
    },
  );

  return fields;
};

export const CreateNewVulnerability = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = React.useState(false);
  const _ref = React.useRef(null);
  const viewRef = React.useRef(null);
  const threatList = useSelector(getThreatsByStatus.bind(null, "active")) || [];
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get("tab");
  const vulnerabilityId = params.get("id");
  const currentVulnerability = useSelector(getCurrentVulnerability);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    dispatch(mitigatorActions.fetchThreats());
  }, []);
  useEffect(() => {
    if (currentVulnerability && viewRef.current) {
      viewRef.current.updateFormData({
        vulnerability_id: currentVulnerability.vulnerability_id,
        category: currentVulnerability.category,
        threat: currentVulnerability.threat,
        vulnerability: currentVulnerability.vulnerability,
      });
    }
  }, [currentVulnerability]);

  useEffect(() => {
    if (!vulnerabilityId) return;
    dispatch(
      mitigatorActions.fetchVulnerability({
        vulnerability_id: vulnerabilityId,
      }),
    );
  }, [vulnerabilityId]);

  useEffect(() => {
    if (
      isEditMode &&
      _ref.current &&
      threatList.length > 0 &&
      currentVulnerability
    ) {
      _ref.current.updateFormData({
        threat: String(currentVulnerability.threat_id),
      });
    }
  }, [isEditMode, threatList, currentVulnerability]);

  const getOptions = (attribute) => {
    let options = [];
    switch (attribute) {
      case "threat":
        options = threatList?.map((threat) => {
          return {
            id: String(threat.id),
            label: threat.threat,
          };
        });
        break;
      default:
        options = [];
        break;
    }
    return options;
  };
  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false); // return to view mode
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      vulnerability_id: values.id,
      vulnerability: values.vulnerability,
      category: values.category,
      threat_id: values.threat,
      status: "draft",
    };
    // const action = mitigatorActions.createVulnerability(body);
    const action = isEditMode
      ? mitigatorActions.updateVulnerability({
        id: vulnerabilityId,
        status: currentVulnerability?.status,
        ...body,
      })
      : mitigatorActions.createVulnerability(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {vulnerabilityId && currentVulnerability && !isEditMode ? (
        <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
          <Row justify="between" align="" className="w-100 pl-16 pr-16">
            <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
              Vulnerability
            </span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>

          <Col className="pl-30 pr-26">
            {/* VIEW MODE FORM */}
            <NMForm
              className="w-100"
              ref={viewRef}
              readOnly={true}
              fields={getVulnerabilityFields(true, false)}
              formData={{
                vulnerability_id: currentVulnerability.vulnerability_id,
                category: currentVulnerability.category,
                threat: currentVulnerability.threat,
                vulnerability: currentVulnerability.vulnerability,
              }}
            />
          </Col>
          <Row
            className="pr-36 mt-16 w-100"
            justify="end"
            align="center"
            style={{ gap: "12px" }}
          >
            {currentTab === "draft" && (
              <>
                <Button
                  label="Edit"
                  icon="EDIT"
                  onClick={() => setIsEditMode(true)}
                />
                <Button
                  label="Activate"
                  icon="CHECK_CONFIRM"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateVulnerability({
                        id: vulnerabilityId,
                        vulnerability: currentVulnerability.vulnerability,
                        category: currentVulnerability.category,
                        threat_id: currentVulnerability.threat_id,
                        status: "active",
                      }),
                    );
                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
            {currentTab === "active" && (
              <>
                <Button
                  label="Archive"
                  icon="OPEN_FOLDER"
                  variant="CAUTION"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateVulnerability({
                        id: vulnerabilityId,
                        vulnerability: currentVulnerability.vulnerability,
                        category: currentVulnerability.category,
                        threat_id: currentVulnerability.threat_id,
                        status: "archived",
                      }),
                    );
                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
            {currentTab === "archive" && (
              <>
                <Button
                  label="Unarchive"
                  icon="OPEN_FOLDER"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateVulnerability({
                        id: vulnerabilityId,
                        vulnerability: currentVulnerability.vulnerability,
                        category: currentVulnerability.category,
                        threat_id: currentVulnerability.threat_id,
                        status: "draft",
                      }),
                    );
                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
          </Row>
        </Col>
      ) : (
        <>
          <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
            <Row justify="between" align="center" className="w-100 pl-16 pr-16">
              <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
                Vulnerability
              </span>
            </Row>
            <NMForm
              className="w-100 pl-30 pr-26"
              ref={_ref}
              // formData={{ id: "Vulnerability ID" }}
              formData={
                isEditMode
                  ? {
                    vulnerability_id:
                      currentVulnerability?.vulnerability_id ?? "",
                    category: currentVulnerability?.category ?? "",
                    threat: currentVulnerability?.threat ?? "",
                    vulnerability: currentVulnerability?.vulnerability ?? "",
                  }
                  : {
                    vulnerability_id: "",
                    category: "",
                    threat: "",
                    vulnerability: "",
                  }
              }
              id="vulnerability-form"
              fields={
                isEditMode
                  ? getVulnerabilityFields(false, true) // EDIT MODE → show ID
                  : getVulnerabilityFields(false, false) // CREATE MODE → hide ID
              }
              getOptions={getOptions}
            />
            <Row
              className="pr-26 mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                onClick={handleCancel}
                disabled={submitting}
              />
              <Button
                label="Save"
                icon="SAVE"
                onClick={handleSave}
                loading={submitting}
              />
            </Row>
          </Col>
        </>
      )}
    </div>
  );
};
